'use client';

import React from 'react';

interface CountUpProps {
  /** The figure to count to. Anything not numeric renders as-is, unanimated. */
  value: number | string;
  durationMs?: number;
  className?: string;
}

/** Fast at first, settling at the end, rather than a flat linear ramp. */
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * A figure that counts up the first time it is scrolled into view.
 *
 * Deliberately conservative about when it runs:
 *
 * - Once only. Re-running every time the section re-enters the viewport turns
 *   a flourish into a distraction on the way back up the page.
 * - Only when the value is a real number. These figures arrive from the API
 *   after first paint, so the element is often on screen showing a placeholder
 *   before there is anything to count to; it waits for both.
 * - Not at all under prefers-reduced-motion, where it prints the final figure
 *   immediately. A number racing upward is exactly the kind of movement that
 *   setting exists to stop.
 *
 * Renders tabular figures, so the digits do not jostle their neighbours as
 * the width of each changes mid-count.
 */
const CountUp: React.FC<CountUpProps> = ({ value, durationMs = 1200, className = '' }) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  /*
   * The value the count last finished on - not a boolean.
   *
   * It was "has started" once, which stranded the figure at zero: React runs
   * effects twice in development, so the first pass began the count, its
   * cleanup cancelled the pending frame, and the second pass saw the flag set
   * and returned without restarting.
   *
   * A boolean "has finished" was still wrong, because these figures change
   * after they first render. A page starts with zeroes, the real totals
   * arrive from the API seconds later, and a counter that had already settled
   * on the placeholder ignored them - so the card sat at 0 for good. Holding
   * the value means a new target counts again.
   */
  const finishedFor = React.useRef<number | null>(null);
  const [inView, setInView] = React.useState(false);
  const [shown, setShown] = React.useState<number | null>(null);
  // Mirrors `shown` for the effect to read. Depending on the state directly
  // would restart the animation on every frame it sets.
  const shownRef = React.useRef<number | null>(null);
  shownRef.current = shown;

  const target = typeof value === 'number' ? value : Number(value);
  const isNumeric = Number.isFinite(target) && String(value).trim() !== '';

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // No IntersectionObserver (very old browsers): treat it as seen, so the
    // figure is shown rather than stuck at nothing.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      /*
        Fires just before the figure reaches the viewport, not once it is
        already showing. The count has to reset to zero to start, and at a
        visible threshold a reader could catch the final figure for a frame
        before it dropped back - so the reset happens off-screen instead.
      */
      { threshold: 0, rootMargin: '0px 0px 120px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!inView || !isNumeric || finishedFor.current === target) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setShown(target);
      finishedFor.current = target;
      return;
    }

    /*
      Counts from whatever is on screen, not from zero every time.
      
      The first count starts at zero, but a figure that updates later - the
      placeholder giving way to the real total - counts on from where it was
      rather than dropping back to zero and climbing again.
    */
    const from = shownRef.current ?? 0;
    setShown(from);

    let frame = 0;
    const started = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - started) / durationMs, 1);
      setShown(Math.round(from + easeOutCubic(progress) * (target - from)));
      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        finishedFor.current = target;
      }
    };
    frame = requestAnimationFrame(step);

    /*
     * Last resort: print the figure whatever happens to the frame loop.
     *
     * requestAnimationFrame does not run in a hidden tab, so a page opened in
     * the background can reach this point, reset to zero and then sit there.
     * A wrong number is far worse than a missing flourish, so the real value
     * lands regardless once the count should have been over.
     */
    const failsafe = window.setTimeout(() => {
      if (finishedFor.current !== target) {
        setShown(target);
        finishedFor.current = target;
      }
    }, durationMs + 500);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(failsafe);
    };
  }, [inView, isNumeric, target, durationMs]);

  // Non-numeric values - a placeholder while loading, or "N/A" - pass straight
  // through. Before the count starts the target is printed rather than a zero,
  // so a reader who never triggers it still sees the real figure.
  const display = !isNumeric ? value : shown === null ? target : shown;

  return (
    <span data-component-id="src/components/common/CountUp.tsx:149:4" data-component-path="src/components/common/CountUp.tsx" data-component-line="149" data-component-end-line="151" data-component-file="CountUp.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" ref={ref} className={`tabular-nums ${className}`}>
      {display}
    </span>
  );
};

export default CountUp;
